<template>
  <aside class="validity-panel">
    <header class="validity-panel__header">
      <div class="validity-panel__header-top">
        <StatusBadge
          v-if="validity.status"
          :label="validity.status"
          :active="validity.statusValor === FREIGHT_VALIDITY_STATUS.APROVADA"
        />

        <a-button
          class="validity-panel__close"
          type="link"
          aria-label="Fechar detalhes da vigência"
          @click="$emit('close')"
        >
          <a-icon type="close" />
        </a-button>
      </div>

      <h3 class="validity-panel__title">Detalhes da vigência</h3>

      <p class="validity-panel__dates">
        <span class="validity-panel__label">Inicio:</span> {{ validity.inicio }}
        <span class="validity-panel__separator">•</span>
        <span class="validity-panel__label">Fim:</span> {{ validity.fim }}
      </p>
    </header>

    <div class="validity-panel__body">
      <a-radio-group v-model="activeTab" class="validity-panel__tabs">
        <a-radio-button value="calculos">Cálculos</a-radio-button>
        <a-radio-button value="taxas">Taxas</a-radio-button>
      </a-radio-group>

      <a-spin v-if="loading" class="validity-panel__spin" />

      <template v-else-if="activeTab === 'calculos'">
        <a-empty
          v-if="!faixas.length"
          description="Nenhuma faixa cadastrada."
        />

        <div
          v-for="faixa in faixas"
          :key="faixa.rangeId"
          class="validity-panel__range"
        >
          <div class="validity-panel__range-top">
            <StatusBadge
              v-if="faixa.status"
              :label="faixa.status"
              :active="faixa.statusValor === FREIGHT_TABLE_STATUS.ATIVA"
            />
          </div>

          <div v-if="faixa.veiculo" class="validity-panel__range-row">
            <span>
              <span class="validity-panel__label">Veículo:</span>
              {{ faixa.veiculo }}
            </span>
          </div>

          <div class="validity-panel__range-row">
            <span>
              <span class="validity-panel__label">Faixa inicial:</span>
              {{ faixa.valorInicial }}
            </span>
            <span>
              <span class="validity-panel__label">Faixa final:</span>
              {{ faixa.valorFinal }}
            </span>
          </div>

          <p class="validity-panel__range-value">{{ faixa.valor }}</p>

          <div class="validity-panel__range-row">
            <span>
              <span class="validity-panel__label">Valor excedente:</span>
              {{ faixa.valorExcedente }}
            </span>
            <span>
              <span class="validity-panel__label">Fator conversão:</span>
              {{ faixa.fatorConversao }}
            </span>
          </div>

          <div class="validity-panel__range-footer">
            <span>
              <span class="validity-panel__label">Faixa:</span>
              <span class="validity-panel__range-active">{{
                faixa.status
              }}</span>
            </span>
            <span class="validity-panel__range-divider" />
            <span>
              <span class="validity-panel__label">Fixa:</span>
              <span
                :class="
                  faixa.temFixa
                    ? 'validity-panel__range-active'
                    : 'validity-panel__range-negative'
                "
              >
                {{ faixa.fixa }}
              </span>
            </span>
            <span class="validity-panel__range-divider" />
            <span>
              <span class="validity-panel__label">Excedente:</span>
              <span
                :class="
                  faixa.temExcedente
                    ? 'validity-panel__range-active'
                    : 'validity-panel__range-negative'
                "
              >
                {{ faixa.excedente }}
              </span>
            </span>
          </div>
        </div>
      </template>

      <template v-else>
        <a-radio-group
          v-model="activeFeeTab"
          class="validity-panel__tabs"
          size="small"
        >
          <a-radio-button
            v-for="tab in FEE_TABS"
            :key="tab.key"
            :value="tab.key"
          >
            {{ tab.label }}
          </a-radio-button>
        </a-radio-group>

        <template v-if="activeFeeTab === 'custo'">
          <a-empty
            v-if="!custosExtras.length"
            description="Nenhum custo extra informado."
          />

          <div
            v-for="custo in custosExtras"
            :key="custo.id"
            class="validity-panel__extra-cost"
          >
            <p class="validity-panel__extra-cost-title">{{ custo.custo }}</p>
            <div
              v-for="field in EXTRA_COST_FIELDS"
              :key="field.key"
              class="validity-panel__range-row"
            >
              <span>
                <span class="validity-panel__label">{{ field.label }}:</span>
                {{ custo[field.key] }}
              </span>
            </div>
          </div>
        </template>

        <div
          v-for="(group, index) in feeGroups"
          v-else
          :key="index"
          class="validity-panel__fee-group"
        >
          <div
            v-for="field in group"
            :key="field.key"
            class="validity-panel__fee"
            :class="{ 'validity-panel__fee--full': group.length === 1 }"
          >
            <span class="validity-panel__fee-label">{{ field.label }}</span>
            <a-input
              class="validity-panel__fee-input"
              :value="taxas[field.key]"
              :placeholder="field.money ? 'R$ 0,00' : 'Nenhuma informada'"
              read-only
            />
          </div>
        </div>
      </template>
    </div>
  </aside>
</template>

<script>
import StatusBadge from "@/shared/components/StatusBadge.vue";
import { FREIGHT_TABLE_STATUS } from "../../constants/freightTableStatus";
import { FREIGHT_VALIDITY_STATUS } from "../../constants/freightValidityStatus";

// Mesmas abas da tela de criação, em leitura (ADR-0012 §2). Cada item interno é
// uma linha do painel: 2 campos lado a lado, 1 campo ocupa a largura toda.
const FEE_TABS = [
  {
    key: "tda",
    label: "TDA/TDE",
    groups: [
      [
        { key: "tipoTaxaTde", label: "Tipo taxa TDE" },
        { key: "tipoTaxaTda", label: "Tipo taxa TDA" },
      ],
      [
        { key: "taxaTda", label: "Taxa TDA", money: true },
        { key: "valorFixoTda", label: "Valor fixo TDA", money: true },
      ],
      [
        { key: "taxaTde", label: "Taxa TDE", money: true },
        { key: "valorFixoTde", label: "Valor fixo TDE", money: true },
      ],
    ],
  },
  {
    key: "pedagio",
    label: "Pedágio",
    groups: [
      [{ key: "tipoTaxaPedagio", label: "Tipo taxa pedágio" }],
      [
        { key: "taxaPedagio", label: "Pedágio por fração de", money: true },
        { key: "valorPedagio", label: "Valor", money: true },
      ],
    ],
  },
  {
    key: "outros",
    label: "Outros",
    groups: [
      [
        { key: "taxaGris", label: "Taxa gris", money: true },
        { key: "minimoGris", label: "Mínimo gris", money: true },
      ],
      [
        { key: "adValorem", label: "Ad Valorem", money: true },
        { key: "minimoAdValorem", label: "Mínimo advalorem", money: true },
      ],
      [
        { key: "valorMinimoNota", label: "Valor mínimo nota/CTe", money: true },
        { key: "valorTas", label: "Valor TAS", money: true },
      ],
      [
        { key: "valorSecCat", label: "Valor SEC/CAT", money: true },
        { key: "valorDespacho", label: "Valor despacho", money: true },
      ],
      [
        { key: "valorAgendamento", label: "Valor agendamento", money: true },
        { key: "valorOutros", label: "Valor outros", money: true },
      ],
      [
        { key: "taxaDevolucao", label: "Taxa devolução", money: true },
        { key: "taxaReentrega", label: "Taxa reentrega", money: true },
      ],
      [
        { key: "taxaColeta", label: "Taxa de coleta", money: true },
        { key: "valorMinimoRota", label: "Valor mínimo por rota", money: true },
      ],
      [{ key: "quantidadeNotas", label: "Quantidade de notas" }],
    ],
  },
  { key: "custo", label: "Custo", groups: [] },
];

const EXTRA_COST_FIELDS = [
  { key: "trecho", label: "Tipo trecho" },
  { key: "trechoCusto", label: "Tipo trecho custo" },
  { key: "taxa", label: "Taxa" },
  { key: "taxaComissao", label: "Taxa (%)" },
  { key: "valorMinimo", label: "Valor mínimo" },
];

export default {
  name: "FreightTableValidityPanel",
  components: { StatusBadge },
  props: {
    validity: {
      type: Object,
      default: () => ({}),
    },
    details: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      FREIGHT_TABLE_STATUS,
      FREIGHT_VALIDITY_STATUS,
      FEE_TABS,
      EXTRA_COST_FIELDS,
      activeTab: "calculos",
      activeFeeTab: "tda",
    };
  },
  computed: {
    faixas() {
      return this.details ? this.details.faixas : [];
    },
    taxas() {
      return this.details ? this.details.taxas : {};
    },
    custosExtras() {
      return (this.details && this.details.custosExtras) || [];
    },
    feeGroups() {
      const tab = FEE_TABS.find((item) => item.key === this.activeFeeTab);
      return tab ? tab.groups : [];
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.validity-panel {
  position: sticky;
  top: 16px;
  width: 100%;
  height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: @color-bg-panel;
  border: 1px solid @color-border-light;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  &__header {
    padding: 20px 24px 16px 24px;
    border-bottom: 1px solid @color-border-light;
  }

  &__header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__close {
    color: @color-text-medium;
  }

  &__title {
    font-family: @font-primary;
    font-size: 22px;
    font-weight: 600;
    color: @color-text-dark;
    margin: 12px 0 4px 0;
  }

  &__dates {
    font-size: 14px;
    color: @color-text-dark;
    margin: 0;
  }

  &__label {
    color: @color-text-light;
  }

  &__separator {
    color: @color-text-muted;
    margin: 0 6px;
  }

  &__body {
    flex: 1;
    min-height: 0;
    padding: 16px 24px 24px 24px;
    overflow-y: auto;
  }

  &__tabs {
    background-color: @color-bg-subtle;
    padding: 4px;
    border-radius: 8px;
    display: flex;
    width: max-content;
    max-width: 100%;
    margin-bottom: 16px;

    /deep/ .ant-radio-button-wrapper {
      background: transparent;
      border: none;
      box-shadow: none !important;
      color: @color-text-light;
      border-radius: 6px;
      height: 28px;
      line-height: 26px;
      display: flex;
      align-items: center;

      &:not(:first-child)::before {
        display: none;
      }
    }

    /deep/ .ant-radio-button-wrapper-checked {
      background-color: #fff;
      color: @color-text-dark;
      font-weight: 500;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
    }
  }

  &__spin {
    display: block;
    padding: 32px;
    text-align: center;
  }

  &__extra-cost {
    background: #fff;
    border: 1px solid @color-border-light;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
  }

  &__extra-cost-title {
    margin: 0 0 8px;
    font-weight: 500;
    color: @color-text-dark;
  }

  &__range {
    background: #fff;
    border: 1px solid @color-border-light;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
  }

  &__range-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__range-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 14px;
    color: @color-text-dark;
  }

  &__range-value {
    font-size: 20px;
    font-weight: 600;
    color: @color-text-dark;
    margin: 12px 0;
    padding: 12px 0;
    border-top: 1px solid @color-border-light;
    border-bottom: 1px solid @color-border-light;
  }

  &__range-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid @color-border-light;
    font-size: 14px;
    color: @color-text-dark;
  }

  &__range-divider {
    width: 1px;
    height: 14px;
    background-color: @color-border-light;
  }

  &__range-active {
    color: @color-success;
    margin-left: 4px;
  }

  &__range-negative {
    color: @color-danger-strong;
    margin-left: 4px;
  }

  &__fee-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid @color-border-light;

    &:last-child {
      border-bottom: none;
    }
  }

  &__fee-input {
    /deep/ .ant-input {
      background-color: @color-bg-panel;
      border-color: @color-border-light;
      color: @color-text-dark;
      cursor: default;

      &:focus {
        box-shadow: none;
        border-color: @color-border-light;
      }
    }
  }

  &__fee {
    display: flex;
    flex-direction: column;

    &--full {
      grid-column: span 2;
    }
  }

  &__fee-label {
    font-size: 14px;
    color: @color-text-dark;
    margin-bottom: 6px;
  }
}
</style>
